import { ReactNode } from 'react'

import { ResultOf } from '@graphql-typed-document-node/core'
import classNames from 'classnames'

import { CommonSmallHorizontalArrowIcon, CommonBenefitIcon } from '@local/assets/icons'

import BookmarkButton from '@web/components/BookmarkButton/BookmarkButton'
import { ButtonBookmarkType } from '@web/components/BookmarkButton/BookmarkButton.constant'
import Clickable from '@web/components/Clickable'
import MichelinGuideAwards from '@web/components/PlaceAwards/MichelinGuideAwards'
import NaverAwards from '@web/components/PlaceAwards/NaverAwards.federation'
import PlaceSection from '@web/components/PlaceSection/PlaceSection'
import { gql } from '@web/gql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { useGoTo } from '@web/hooks/useGoTo'
import { messages } from '@web/libs/constants'
import { isBookmarkShowable } from '@web/libs/place.helper'
import { parseCategory } from '@web/libs/utils'
import { Nullable, Nullish } from '@web/types'

import styles from './Place.module.scss'

const VisitDetailFragment = gql(`
 fragment VisitDetail_Place on Place {
      id
      type
      locationText
      promotion
      category
      name
      isAvailable
      isBusinessPlace
      hotelGradeText {
        korean
      }
      naverHotelId
      ...BookmarkButton
      ...MichelinGuideAwards_Place
      ...UseGoToPlaceDetail_Place
    }
`)

interface Props {
  showHeaderTitle: boolean
  place: ResultOf<typeof VisitDetailFragment>
  isTrainTicket?: Nullish<boolean>
  displayName: Nullable<ReactNode>
  fullView?: boolean
  clickCodes: {
    place: string
    save: string
    unsave: string
  }
}

const Place = ({ showHeaderTitle, place, isTrainTicket, displayName, fullView, clickCodes }: Props) => {
  const { goToPlaceDetail } = useGoTo()
  const { mapAppVersion } = useGlobalStaticStateContext()

  const isValid = !!place?.isAvailable && !place.isBusinessPlace && !isTrainTicket
  const locationText = place?.locationText

  const hotelGradeText = place?.hotelGradeText?.korean

  return (
    <PlaceSection className={styles.place_info_section}>
      <div className={classNames(styles.info_area, { [styles.type_full_view]: fullView })}>
        <div className={styles.tit_wrapper}>
          <Clickable
            className={styles.tit_area}
            onClick={() => goToPlaceDetail(place, mapAppVersion)}
            disabled={!isValid}
            nClickCode={clickCodes.place}
          >
            <div className={styles.inner}>
              <span
                className={classNames(styles.name, {
                  [styles.type_ellipsis]: !isTrainTicket,
                })}
              >
                {displayName ?? messages.NONE_PLACE_ADDRESS}
              </span>
              {isValid && (
                <span className={styles.icon_wrapper}>
                  <span className="place_blind">더보기</span>
                  <CommonSmallHorizontalArrowIcon className={styles.icon} aria-hidden="true" />
                </span>
              )}
            </div>
            <div className={styles.etc_area}>
              {!place?.isBusinessPlace && place?.category && (
                <span className={styles.item}>
                  {isTrainTicket ? (
                    '기차 이용 내역'
                  ) : (
                    <>
                      {parseCategory(place.category)}
                      {hotelGradeText ? ` ${hotelGradeText}` : null}
                    </>
                  )}
                </span>
              )}
              {isValid ? (
                locationText && <span className={styles.item}>{locationText}</span>
              ) : (
                <span className={styles.item}>{messages.NOT_AVAILABLE_PLACE}</span>
              )}
            </div>
          </Clickable>
          {isBookmarkShowable(place) && !isTrainTicket && (
            <div className={classNames(styles.btn_bookmark, styles.type_no_padding)}>
              <BookmarkButton
                place={place}
                showLabel
                nClickCodes={{
                  save: clickCodes.save,
                  unsave: clickCodes.unsave,
                }}
                type={ButtonBookmarkType.REVIEW_END}
              />
            </div>
          )}
        </div>
        {place && <MichelinGuideAwards place={place} />}
        {place?.id && <NaverAwards placeId={place.id} />}
        {place?.promotion ? (
          <div className={styles.benefit_area}>
            <span className={styles.label_area}>
              <CommonBenefitIcon className={styles.icon} aria-hidden="true" />
              혜택
            </span>
            {place?.promotion}
          </div>
        ) : null}
      </div>
    </PlaceSection>
  )
}

export default Place
